import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Radio, Square } from "lucide-react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LiveSessionStatus,
  LiveWindowStatus,
  useAdminLiveWindowsQuery,
  useAdminLiveSessionsQuery,
  useAdminLiveWatchQuery,
  useAdminStopLiveSessionMutation,
} from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/lib/format";
import { formatWindowRange } from "@/lib/live-watch/formatWindowRange";
import { mapLiveWatchError } from "@/lib/live-watch/mapLiveWatchError";

const PAGE_SIZE = 25;
const WINDOWS_PAGE_SIZE = 25;

const statusVariant = (status: LiveSessionStatus): "default" | "secondary" | "destructive" | "outline" => {
  if (status === LiveSessionStatus.Live || status === LiveSessionStatus.Failed) {
    return "destructive";
  }
  if (status === LiveSessionStatus.Pending) return "secondary";
  return "outline";
};

const windowStatusVariant = (
  status: LiveWindowStatus,
): "default" | "secondary" | "destructive" | "outline" => {
  if (status === LiveWindowStatus.Failed) return "destructive";
  if (status === LiveWindowStatus.Pending || status === LiveWindowStatus.Curating) {
    return "secondary";
  }
  return "outline";
};

const AdminLiveWatchDetail = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const { data: watchData, loading: watchLoading, error: watchError, refetch: refetchWatch } =
    useAdminLiveWatchQuery({ variables: { id }, skip: !id });
  const {
    data: sessionsData,
    loading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
  } = useAdminLiveSessionsQuery({
    variables: { watchId: id, page, pageSize: PAGE_SIZE },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });
  const [stopSession, { loading: stopping }] = useAdminStopLiveSessionMutation();

  const watch = watchData?.adminLiveWatch;
  const sessions = sessionsData?.adminLiveSessions.data ?? [];
  const totalPages = sessionsData?.adminLiveSessions.pageCount ?? 1;
  const latestSession = sessions[0];
  const {
    data: windowsData,
    loading: windowsLoading,
    error: windowsError,
  } = useAdminLiveWindowsQuery({
    variables: { sessionId: latestSession?.id ?? "", page: 1, pageSize: WINDOWS_PAGE_SIZE },
    skip: !latestSession,
    fetchPolicy: "cache-and-network",
  });
  const windows = windowsData?.adminLiveWindows.data ?? [];

  const handleStop = async (sessionId: string) => {
    try {
      await stopSession({ variables: { id: sessionId } });
      toast({ title: t("admin.liveWatches.toast.stopped") });
      await Promise.all([refetchWatch(), refetchSessions()]);
    } catch (stopError) {
      console.error("Failed to stop live session:", stopError);
      toast({
        title: t("admin.liveWatches.toast.stopError"),
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button asChild variant="ghost" size="icon">
                <Link to="/admin/live-watches" aria-label={t("admin.liveWatches.back")}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
              <Radio className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">
                  {watch?.youtube_channel?.name ?? t("admin.liveWatches.detailTitle")}
                </h1>
                <p className="text-muted-foreground">
                  {watch
                    ? `${watch.platform} · ${t(`admin.liveWatches.kinds.${watch.kind}`)}`
                    : t("admin.liveWatches.detailSubtitle")}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="border-red-500 text-red-500">ADMIN</Badge>
          </div>

          {watchLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : watchError || !watch ? (
            <Card>
              <CardContent className="py-8 text-center text-destructive">
                {t("admin.liveWatches.loadError")}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.liveWatches.configuration")}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-muted-foreground">{t("admin.liveWatches.fields.identity")}</p>
                  <p className="break-all font-medium">
                    {watch.source_url || watch.platform_channel_id || watch.platform_login || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t("admin.liveWatches.fields.windowMinutes")}</p>
                  <p className="font-medium">{watch.window_minutes}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t("admin.liveWatches.fields.retentionDays")}</p>
                  <p className="font-medium">{watch.retention_days}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t("admin.liveWatches.fields.overlapSeconds")}</p>
                  <p className="font-medium">{watch.overlap_seconds}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{t("admin.liveWatches.sessions")}</CardTitle>
            </CardHeader>
            <CardContent>
              {sessionsLoading && !sessionsData ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                </div>
              ) : sessionsError ? (
                <div className="py-8 text-center text-destructive">
                  {t("admin.liveWatches.sessionsLoadError")}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.liveWatches.fields.status")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.ingestStatus")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.platformLiveId")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.startedAt")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.heartbeatAt")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.endedAt")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.retentionExpiresAt")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.lastError")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sessions.map((session) => (
                          <TableRow key={session.id}>
                            <TableCell>
                              <Badge variant={statusVariant(session.status)}>
                                {t(`admin.liveWatches.statuses.${session.status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {t(`admin.liveWatches.ingestStatuses.${session.ingest_status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>{session.platform_live_id ?? "-"}</TableCell>
                            <TableCell>
                              {session.started_at ? formatDateTime(session.started_at) : "-"}
                            </TableCell>
                            <TableCell>
                              {session.heartbeat_at ? formatDateTime(session.heartbeat_at) : "-"}
                            </TableCell>
                            <TableCell>
                              {session.ended_at ? formatDateTime(session.ended_at) : "-"}
                            </TableCell>
                            <TableCell>
                              {session.retention_expires_at
                                ? formatDateTime(session.retention_expires_at)
                                : "-"}
                            </TableCell>
                            <TableCell className="max-w-xs break-words text-destructive">
                              {mapLiveWatchError(session.last_error, t) ?? "-"}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {session.live_url && (
                                  <Button asChild variant="ghost" size="sm">
                                    <a href={session.live_url} target="_blank" rel="noopener noreferrer">
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                                {session.status === LiveSessionStatus.Live && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    disabled={stopping}
                                    onClick={() => handleStop(session.id)}
                                  >
                                    <Square className="mr-2 h-3 w-3" />
                                    {t("admin.liveWatches.stopSession")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {sessions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                              {t("admin.liveWatches.sessionsEmpty")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                      >
                        {t("admin.liveWatches.previous")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      >
                        {t("admin.liveWatches.next")}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {latestSession && (
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.liveWatches.windows")}</CardTitle>
              </CardHeader>
              <CardContent>
                {windowsLoading && !windowsData ? (
                  <div className="flex justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                  </div>
                ) : windowsError ? (
                  <div className="py-8 text-center text-destructive">
                    {t("admin.liveWatches.sessionsLoadError")}
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.liveWatches.fields.windowRange")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.status")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.library")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.lastError")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {windows.map((window) => (
                          <TableRow key={window.id}>
                            <TableCell className="tabular-nums">
                              {formatWindowRange(window.window_start, window.window_end)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={windowStatusVariant(window.status)}>
                                {t(`admin.liveWatches.windowStatuses.${window.status}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {window.library_document_id ? (
                                <Link
                                  className="text-primary underline-offset-4 hover:underline"
                                  to={`/library/${window.library_document_id}/clips`}
                                >
                                  {window.library_document_id}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="max-w-xs break-words text-destructive">
                              {mapLiveWatchError(window.last_error, t) ?? "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {windows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                              {t("admin.liveWatches.windowsEmpty")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default AdminLiveWatchDetail;
